// "Choose your plan" (plan v4 items 2 and 5): the book's 3, 4 or 5-day routine, the glute
// focus plan, or Running + HIIT; pick the weekdays; review exactly what changes; apply once.
// Nothing saves until "Apply this plan". History and phase progress always stay.
import { ROUTINES, buildPlanPreview, weekdayClash, LEG_ROLES } from '../lib/bls-routines.js';
import { proposeRunMoves, applyRunMoves } from '../lib/run-schedule.js';
import { loadExerciseLibrary, loadProgramDays, loadDayExercises, loadRunPlan, applyPlan, saveRunSchedule } from '../lib/grit-data.js';
import { activeUser } from '../lib/auth.js';
import { displayNameHtml } from '../lib/exercise-names.js';
import { scheduleFor } from '../screens/running.js';
import { confirmBox } from './dialog.js';
import { toast } from './toast.js';
import { icon } from './icons.js';

const WD = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WDL = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const NOTE = {
  kept: (s) => `<span class="grit-chip-success">Kept${s.startWeight != null ? ` ${s.startWeight} lb` : ''}</span>`,
  'your-swap': (s) => `<span class="grit-chip-accent">Your swap${s.startWeight != null ? `, ${s.startWeight} lb` : ''}</span>`,
  check: (s) => `<span class="grit-chip bg-[#FBF3E0] text-[#7A570F]">Check: ${s.startWeight} lb?</span>`,
  new: () => '<span class="grit-chip bg-fill text-ink">New, set weight</span>',
  optional: () => '<span class="grit-chip bg-fill text-ink">Your extra</span>',
};

export async function openPlanChooser({ onApplied } = {}) {
  const me = activeUser();
  const overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 z-[110] flex items-end justify-center bg-black/30';
  overlay.innerHTML = '<div class="grit-sheet overflow-auto"><div class="grit-sheet-handle"></div><p class="text-inksoft text-[15px] py-6 text-center">Loading your plan…</p></div>';
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  document.body.appendChild(overlay);
  const close = () => overlay.remove();

  const [library, days, runPlan] = await Promise.all([loadExerciseLibrary(), loadProgramDays(me), loadRunPlan(me)]);
  const oldDays = await Promise.all(days.map(async (d) => ({ ...d, exercises: d.kind === 'lift' ? await loadDayExercises(d.id) : [] })));
  const schedule = scheduleFor(runPlan, me);

  let key = (days.filter((d) => d.kind === 'lift').length === 3 ? 'bls3' : days.filter((d) => d.kind === 'lift').length === 4 ? 'bls4' : 'bls5');
  let weekdays = {};
  let busy = false;

  function paint() {
    const preview = buildPlanPreview(key, { oldDays, library, weekdays });
    const clash = weekdayClash(preview);
    const moves = proposeRunMoves(schedule, preview.days.map((d) => ({ weekday: d.weekday, isLegs: LEG_ROLES.has(d.role) })));
    const isBls = key.startsWith('bls');

    const choice = (k, title, sub) => `<button data-plan="${k}" class="grit-row w-full text-left rounded-2xl p-4 ${(k === 'bls' ? isBls : key === k) ? 'bg-grit-soft' : 'bg-pagebg'}">
      <div class="text-[17px] font-semibold text-ink">${title}</div><div class="text-[13px] text-[#5E5E63] mt-0.5">${sub}</div></button>`;

    const dayCards = preview.days.map((d, di) => `
      <div class="grit-card !bg-pagebg p-4 mt-2">
        <div class="flex items-center justify-between gap-2">
          <div class="text-[17px] font-semibold text-ink">${d.name}</div>
          <select data-wd="${di}" aria-label="Day for ${d.name}" class="grit-input !w-auto !min-h-[44px] !py-2 !bg-card font-semibold">${WD.map((w, i) => (i ? `<option value="${i}" ${d.weekday === i ? 'selected' : ''}>${w}</option>` : '')).join('')}</select>
        </div>
        ${d.slots.length ? d.slots.map((s) => `<div class="flex items-center justify-between gap-2 py-1.5 border-t border-hairline first:border-0 mt-1">
          <div class="min-w-0"><div class="text-[15px] text-ink truncate">${displayNameHtml(s.exercise)}</div><div class="text-[13px] text-inksoft">${s.sets} sets · ${s.lo}-${s.hi} reps</div></div>
          <div class="flex-none">${(NOTE[s.note] || NOTE.new)(s)}</div></div>`).join('')
        : '<div class="text-[15px] text-inksoft mt-1">Do your HIIT video, then check it off.</div>'}
      </div>`).join('');

    const moveList = moves.length ? `<div class="grit-card !bg-[#FBF3E0] p-4 mt-3">
        <div class="text-[15px] font-semibold text-[#7A570F]">Your runs move off leg days (book p.358)</div>
        ${moves.map((m) => `<div class="text-[15px] text-ink mt-1">${WDL[m.from]} run → ${WDL[m.to]}</div>`).join('')}
      </div>` : '';

    overlay.firstElementChild.innerHTML = `
      <div class="grit-sheet-handle"></div>
      <div class="flex items-start justify-between">
        <div><div class="text-[20px] font-bold tracking-tight text-ink">Choose your plan</div>
          <div class="text-[13px] text-inksoft">Review everything below. Nothing changes until you apply it.</div></div>
        <button data-close aria-label="Close" class="text-inksoft w-11 h-11 -mr-2 flex items-center justify-center flex-none">${icon.x('w-5 h-5')}</button>
      </div>
      <div class="space-y-2 mt-4">
        ${choice('bls', 'Bigger Leaner Stronger', "The book's routine for how many days you lift (p.316-320)")}
        ${isBls ? `<div class="grit-seg">${['bls3', 'bls4', 'bls5'].map((k) => `<button data-days="${k}" class="${key === k ? 'on' : ''}">${ROUTINES[k].short}</button>`).join('')}</div>` : ''}
        ${choice('glute3', 'Glute focus, 3 days', 'Two lower days led by glute work, one upper day. A starting point, not from the book; edit anything.')}
        ${choice('runhiit', 'Running + HIIT', 'Two HIIT days; runs come from your running plan')}
      </div>
      <div class="text-[13px] text-inksoft mt-4">${preview.name} · ${preview.source}</div>
      ${dayCards}
      ${moveList}
      ${clash ? `<div class="text-[15px] font-semibold text-danger mt-3">Two workouts are on ${WDL[clash]}. Give each its own day.</div>` : ''}
      <div class="text-[13px] text-inksoft mt-3">Your workout history and training phase stay. "Kept" carries your weight and progress; "Check" means the lift or range changed, so confirm the weight on your first set.</div>
      <div class="flex gap-2 mt-4">
        <button data-close class="grit-btn-quiet flex-1">Cancel</button>
        <button id="apply-plan" class="grit-btn flex-1" ${clash || busy ? 'disabled' : ''}>${busy ? 'Applying…' : 'Apply this plan'}</button>
      </div>`;

    overlay.querySelectorAll('[data-close]').forEach((b) => { b.onclick = close; });
    overlay.querySelectorAll('[data-plan]').forEach((b) => { b.onclick = () => { const k = b.dataset.plan; key = k === 'bls' ? (isBls ? key : 'bls5') : k; weekdays = {}; paint(); }; });
    overlay.querySelectorAll('[data-days]').forEach((b) => { b.onclick = () => { key = b.dataset.days; weekdays = {}; paint(); }; });
    overlay.querySelectorAll('[data-wd]').forEach((sel) => { sel.onchange = () => { weekdays[sel.dataset.wd] = Number(sel.value); paint(); }; });
    overlay.querySelector('#apply-plan').onclick = async () => {
      if (busy || clash) return;
      if (!(await confirmBox({ title: `Switch to ${preview.name}?`, message: 'Your history stays. Your old plan is kept, just switched off.', confirmLabel: 'Apply' }))) return;
      busy = true; paint();
      const ok = await applyPlan(preview);
      let runsOk = true;
      if (ok && moves.length) runsOk = await saveRunSchedule(applyRunMoves(schedule, moves));
      busy = false;
      if (!ok) { toast("Couldn't apply the plan. Nothing changed, try again", { tone: 'bad' }); paint(); return; }
      close();
      toast(runsOk ? 'New plan is on' : 'Plan is on, but your runs did not move; edit them on Running', { tone: runsOk ? 'good' : 'bad' });
      onApplied?.();
    };
  }

  paint();
}
